// Simple condition evaluator to support docs/fields `condition` logic
// Supports: &&, ||, ==, =, !=, dot-paths, and unquoted literals (including spaces)
// Special coercions: boolean true/false <-> 'yes'/'no'

export interface ConditionContext {
  [key: string]: any;
}

function getValueByPath(obj: any, path: string): any {
  const parts = path.split('.').map(p => p.trim()).filter(Boolean);
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return undefined;
    cur = cur[p];
  }
  return cur;
}

function coerceComparable(val: any): string | number | boolean | null {
  if (typeof val === 'boolean') return val ? 'yes' : 'no';
  if (val === null || val === undefined) return null;
  if (typeof val === 'number') return val;
  return String(val);
}

function normalizeLiteral(lit: string): string | number | boolean | null {
  const trimmed = lit.trim();
  if (!trimmed) return '';
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const lower = trimmed.toLowerCase();
  if (lower === 'true' || lower === 'yes') return 'yes';
  if (lower === 'false' || lower === 'no') return 'no';
  return trimmed;
}

// Split an expression by top-level && and ||, preserving operators
function tokenizeExpression(expr: string): Array<{ type: 'segment' | 'op'; value: string }> {
  const out: Array<{ type: 'segment' | 'op'; value: string }> = [];
  let i = 0;
  let buf = '';
  while (i < expr.length) {
    const two = expr.slice(i, i + 2);
    if (two === '&&' || two === '||') {
      if (buf.trim()) out.push({ type: 'segment', value: buf.trim() });
      out.push({ type: 'op', value: two });
      buf = '';
      i += 2;
      continue;
    }
    buf += expr[i];
    i += 1;
  }
  if (buf.trim()) out.push({ type: 'segment', value: buf.trim() });
  return out;
}

function evalSegment(segment: string, ctx: ConditionContext): boolean {
  // Support ==, =, != comparisons; values can contain spaces
  const match = segment.match(/^(.*?)\s*(==|=|!=)\s*(.*)$/);
  if (!match) {
    // If no operator, interpret presence/truthy of variable
    const left = segment.trim();
    const v = getValueByPath(ctx, left);
    return Boolean(v);
  }
  const leftPath = match[1].trim();
  const op = match[2];
  const rightRaw = match[3];

  const leftVal = coerceComparable(getValueByPath(ctx, leftPath));
  const rightVal = normalizeLiteral(rightRaw);

  if (op === '!=' ) {
    return String(leftVal) !== String(rightVal);
  }
  // treat single '=' as '=='
  return String(leftVal) === String(rightVal);
}

export function evaluateCondition(condition: string | undefined, context: ConditionContext): boolean {
  if (!condition || !condition.trim()) return true; // show by default when no condition
  try {
    const tokens = tokenizeExpression(condition);
    if (tokens.length === 0) return true;

    // Evaluate left-to-right with && and || (no precedence beyond token order)
    let acc: boolean | null = null;
    let pendingOp: '&&' | '||' | null = null;
    for (const t of tokens) {
      if (t.type === 'op') {
        pendingOp = t.value as '&&' | '||';
        continue;
      }
      const segVal = evalSegment(t.value, context);
      if (acc === null) {
        acc = segVal;
      } else if (pendingOp === '&&') {
        acc = acc && segVal;
      } else if (pendingOp === '||') {
        acc = acc || segVal;
      } else {
        // no operator, default AND
        acc = acc && segVal;
      }
      pendingOp = null;
    }
    return Boolean(acc);
  } catch (e) {
    // On any parse error, fail-open to avoid hiding fields unexpectedly
    return true;
  }
}

// Build a default context from current form values and known aliases
export function buildConditionContext(formValues: Record<string, any>): ConditionContext {
  const ctx: ConditionContext = { ...formValues };
  // 仅提供中文国家名：orders.nation
  ctx.orders = {
    nation: typeof formValues.nation === 'string' ? formValues.nation : undefined,
  };
  return ctx;
}
